import { field } from './wire';

export type LayoutControlDisplayMode = 'layout-only' | 'hero-below' | 'side-panel';

export type LayoutControlsDashboardType = 'layout' | 'floorplan' | 'grid' | 'bim' | 'scada';

export function isLayoutControlsDashboardType(layoutType: unknown): layoutType is LayoutControlsDashboardType {
  return (
    layoutType === 'layout' ||
    layoutType === 'floorplan' ||
    layoutType === 'grid' ||
    layoutType === 'bim' ||
    layoutType === 'scada'
  );
}

/**
 * The layout config bag as it arrives (`dashboard.layoutConfig`, untyped JSON by
 * the hub's own model) and the widget rows around it: read through `field()`, so
 * each function states the key it wants instead of widening the bag to `any`.
 */
export function getLayoutDisplayMode(layoutConfig: unknown): LayoutControlDisplayMode {
  const mode = field(layoutConfig, 'displayMode');
  if (mode === 'hero-below' || mode === 'side-panel') {
    return mode;
  }
  return 'layout-only';
}

export function getBelowColumns(layoutConfig: unknown, fallback: number = 2): number {
  return Math.max(1, Math.min(4, Number(field(layoutConfig, 'belowColumns')) || fallback));
}

function getFloorPlanOrder(widget: unknown): number {
  const order = Number(field(field(widget, 'widgetConfig'), 'order') ?? field(widget, 'positionY') ?? 0);
  return Number.isFinite(order) ? order : 0;
}

export function getLegacyFloorPlanLayoutControlIds(widgets: unknown[]): string[] {
  return [...widgets]
    .filter((widget) => field(field(widget, 'widgetConfig'), 'floorplanRegion') === 'below')
    .sort((left, right) => {
      const orderDiff = getFloorPlanOrder(left) - getFloorPlanOrder(right);
      if (orderDiff !== 0) return orderDiff;
      return String(field(left, 'id')).localeCompare(String(field(right, 'id')));
    })
    .map((widget) => String(field(widget, 'id')));
}

export function normalizeLayoutControlIds(ids: unknown[]): string[] {
  const seen = new Set<string>();
  return ids.reduce<string[]>((result, value) => {
    const normalized = String(value);
    if (!normalized || seen.has(normalized)) return result;
    seen.add(normalized);
    result.push(normalized);
    return result;
  }, []);
}

interface GetLayoutControlIdsOptions {
  includeLegacyFloorPlanBelow?: boolean;
}

export function getLayoutControlIds(
  layoutConfig: unknown,
  widgets: unknown[] = [],
  options: GetLayoutControlIdsOptions = {},
): string[] {
  const layoutControls = field(layoutConfig, 'layoutControls');
  if (Array.isArray(layoutControls)) {
    return normalizeLayoutControlIds(layoutControls);
  }

  const secondaryControls = field(layoutConfig, 'secondaryControls');
  if (Array.isArray(secondaryControls)) {
    return normalizeLayoutControlIds(secondaryControls);
  }

  if (options.includeLegacyFloorPlanBelow) {
    return getLegacyFloorPlanLayoutControlIds(widgets);
  }

  return [];
}
